import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import { Modal, Button } from "react-bootstrap";
import TablePagination from "./TablePagination";
import {
  getCategories,
  saveCategory,
  updateCategory,
  deleteCategory,
} from "../Apis/CategoryAPIs";
import usePermissions from "../hook/userPermission";

const CategoryLayer = () => {
  const { hasPermission } = usePermissions();
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editingId, setEditingId] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Delete Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const fetchCategories = async () => {
    setLoading(true);
    const params = {
      page: currentPage,
      limit: rowsPerPage,
    };
    if (debouncedSearchTerm) params.search = debouncedSearchTerm;

    const res = await getCategories(params);
    if (res.status) {
      const data = res.response?.data || res.response;
      setCategories(Array.isArray(data) ? data : []);
      setTotalRecords(res.response?.total || (Array.isArray(data) ? data.length : 0));
    }
    setLoading(false);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    fetchCategories();
  }, [debouncedSearchTerm, currentPage, rowsPerPage]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) {
      setError("Exhibition Type is Required");
      return;
    }

    let res;
    if (editingId) {
      res = await updateCategory(editingId, { name: newCategory.trim() });
    } else {
      res = await saveCategory({ name: newCategory.trim() });
    }

    if (res.status) {
      toast.success(`Exhibition Type ${editingId ? "Updated" : "Saved"} Successfully`);
      setNewCategory("");
      setEditingId(null);
      setError("");
      fetchCategories();
    }
  };

  const handleDeleteClick = (cat) => {
    setItemToDelete(cat);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    setIsUpdating(true);
    const res = await deleteCategory(itemToDelete._id || itemToDelete.id);
    if (res.status) {
      toast.success("Exhibition Type Deleted Successfully");
      fetchCategories();
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
    setIsUpdating(false);
  };

  const totalPages = Math.ceil(totalRecords / rowsPerPage);

  return (
    <div className="row gy-4">
      <div className="col-12">
        <div className="card h-100 p-0 radius-12 shadow-sm">
          <div
            className="card-header border-bottom py-12 px-24 d-flex align-items-center justify-content-between"
            style={{
              background: "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)",
            }}
          >
            <h6 className="text-white mb-0">Exhibition Type List</h6>
            <div className="d-flex align-items-center gap-3">
              <div className="navbar-search position-relative">
                <input
                  type="text"
                  className="form-control radius-8 ps-40 py-10 bg-white border-0"
                  style={{ height: "44px" }}
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(0);
                  }}
                />
                <Icon
                  icon="solar:magnifer-linear"
                  className="position-absolute text-secondary-light"
                  style={{
                    left: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: "18px"
                  }}
                />
              </div>
            </div>
          </div>
          <div className="card-body">
            {(hasPermission("category", "add") || (editingId && hasPermission("category", "edit"))) && (
              <div style={{ marginBottom: "20px" }}>
                <form
                  onSubmit={handleSave}
                  className="d-flex gap-3 align-items-start flex-wrap"
                >
                  <div className="flex-grow-1" style={{ minWidth: "250px" }}>
                    <label className="form-label fw-bold">
                      Exhibition Type <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control radius-8 ${error ? "is-invalid" : ""}`}
                      placeholder="Enter Exhibition Type (e.g. Electronics)"
                      value={newCategory}
                      onChange={(e) => {
                        setNewCategory(e.target.value);
                        if (error) setError("");
                      }}
                    />
                    {error && <div className="invalid-feedback">{error}</div>}
                  </div>
                  <div className="d-flex align-items-center gap-3 mt-32">
                    <button
                      type="submit"
                      className="btn radius-8 px-32 py-11 d-flex align-items-center gap-2"
                      style={{
                        backgroundColor: "#f7e7da",
                        color: "black",
                        fontWeight: "600",
                      }}
                    >
                      <Icon icon={editingId ? "solar:save-bold" : "ic:baseline-plus"} className="text-xl" />
                      {editingId ? "Update Exhibition ​Type" : "Add Exhibition ​Type"}
                    </button>
                    {editingId && (
                      <button
                        type="button"
                        className="btn btn-secondary radius-8 px-24 py-11"
                        onClick={() => {
                          setEditingId(null);
                          setNewCategory("");
                        }}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              </div>
            )}
            <div
              className="table-responsive scroll-sm"
              style={{ maxHeight: "400px", overflowY: "auto" }}
            >
              <table className="table bordered-table sm-table mb-0">
                <thead
                  style={{
                    backgroundColor: "#f7e7da",
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  <tr>
                    <th scope="col" style={{ color: "black", fontWeight: "bold", width: "100px" }}>S.No</th>
                    <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Exhibition Type</th>
                    <th scope="col" style={{ color: "black", fontWeight: "bold", width: "150px" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="3" className="text-center py-24">Loading...</td></tr>
                  ) : categories.length > 0 ? (
                    categories.map((cat, index) => (
                      <tr key={cat._id || cat.id}>
                        <td>{currentPage * rowsPerPage + index + 1}</td>
                        <td>
                          <span className="text-md mb-0 fw-medium text-secondary-light">
                            {cat.name}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-10">
                            {hasPermission("category", "edit") && (
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingId(cat._id || cat.id);
                                  setNewCategory(cat.name);
                                  window.scrollTo({ top: 0, behavior: "smooth" });
                                }}
                                className="btn-edit-custom fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                              >
                                <Icon icon="lucide:edit" className="menu-icon" />
                              </button>
                            )}
                            {hasPermission("category", "delete") && (
                              <button
                                type="button"
                                onClick={() => handleDeleteClick(cat)}
                                className="w-36-px h-36-px bg-danger-focus text-danger-main rounded-circle d-flex justify-content-center align-items-center border-0"
                                title="Delete"
                              >
                                <Icon
                                  icon="lucide:trash-2"
                                  style={{ fontSize: "18px" }}
                                />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="3" className="text-center py-24 text-secondary-light">No exhibition types found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <TablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value));
                setCurrentPage(0);
              }}
              totalRecords={totalRecords}
            />
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Body className="text-center p-5">
          <div className="d-flex justify-content-center mb-3">
            <div className="bg-danger-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px">
              <Icon icon="solar:trash-bin-trash-bold" className="text-danger-600 text-xxl" />
            </div>
          </div>
          <h5 className="mb-2">Are you sure?</h5>
          <p className="text-secondary-light mb-4">
            Do you want to delete exhibition type "{itemToDelete?.name}"?
          </p>
          <div className="d-flex justify-content-center gap-3 mt-4">
            <Button variant="outline-secondary" className="px-32 radius-8" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
            <Button className="px-32 radius-8 border-0" onClick={confirmDelete} disabled={isUpdating} style={{ backgroundColor: "#ed1c24", color: "white" }}>
              {isUpdating ? "Deleting..." : "Confirm"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default CategoryLayer;
